import React, { useState } from 'react';

import { ISaveIncomeSourcesRequest } from 'api/curo/ApplicationProcessApi';
import { ButtonWithLoadingState } from 'components/curo-components/common/Button';
import IncomeSourcesForm, {
  IIncomeSourcesFormValues,
  IncomeSourcesFields,
} from 'components/curo-components/forms/IncomeSourcesForm';
import { EmploymentType } from 'components/curo-components/forms/IncomeSourcesForm/constants';
import { getApplicationSelector } from 'handlers/curo-handlers/selectors';
import { useAppSelector } from 'hooks/reduxHooks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { saveIncomeSources } from 'thunks';
import styled from 'types/AppTheme';

const AddIncomeSourceButton = styled.button`
  display: block;
  outline: none;
  background-color: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.accentColor};
  margin-bottom: 36px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
`;

interface IIncomeSourceItem {
  isValid: boolean;
  submit: (() => Promise<IIncomeSourcesFormValues>) | null;
  formData: IIncomeSourcesFormValues;
}

const INITIAL_INCOME_SOURCE: IIncomeSourceItem = {
  isValid: false,
  submit: null,
  formData: {
    [IncomeSourcesFields.GrossMonthlyIncome]: null,
    [IncomeSourcesFields.EmploymentType]: EmploymentType.Employed,
    [IncomeSourcesFields.NextPayDate]: '',
    [IncomeSourcesFields.PaycheckFrequency]: '',
    [IncomeSourcesFields.SourceOfIncome]: '',
  },
};

const MAX_INCOME_SOURCES = 3;

interface IIncomeSourcesStepProps {
  onStepComplete: () => void;
}

const IncomeSourcesStep = ({ onStepComplete }: IIncomeSourcesStepProps) => {
  const [incomeSources, setIncomeSources] = useState<Array<IIncomeSourceItem>>([
    INITIAL_INCOME_SOURCE,
  ]);
  const application = useAppSelector(getApplicationSelector);
  const dispatchWithUnwrap = useDispatchWithUnwrap();

  const [mainIncomeSource] = incomeSources;

  const handleIsValidChange = (
    isValid: boolean,
    submitFunction: () => Promise<IIncomeSourcesFormValues>,
    target: typeof incomeSources[number],
  ) => {
    setIncomeSources((prev) =>
      prev.map((prevIncomeSource) =>
        prevIncomeSource === target
          ? { ...prevIncomeSource, isValid, submit: submitFunction }
          : prevIncomeSource,
      ),
    );
  };

  const handleDataChange = (
    values: IIncomeSourcesFormValues,
    target: typeof incomeSources[number],
  ) => {
    setIncomeSources((prev) =>
      prev.map((prevIncomeSource) =>
        prevIncomeSource === target ? { ...prevIncomeSource, formData: values } : prevIncomeSource,
      ),
    );
  };

  const handleAddAdditionalIncomeSource = () => {
    setIncomeSources((prev) => [...prev, INITIAL_INCOME_SOURCE]);
  };

  const handleRemoveAdditionalIncomeSource = (target: typeof incomeSources[number]) => {
    setIncomeSources((prev) => prev.filter((incomeSource) => incomeSource !== target));
  };

  const isAllFormsValid = incomeSources.every((item) => item.isValid && item.submit !== null);

  const handleContinue = async () => {
    if (isAllFormsValid && application) {
      const result = await Promise.all(
        incomeSources.map((incomeSource) => incomeSource.submit?.()),
      );

      const incomeSourcesData: ISaveIncomeSourcesRequest = {
        incomeSources: result.map((incomeSource) => ({
          employmentType: incomeSource!.employmentType,
          grossMonthlyIncome: incomeSource!.grossMonthlyIncome!,
          nextPayDate: incomeSource!.nextPayDate!,
          paycheckFrequency: incomeSource!.paycheckFrequency,
          sourceOfIncome:
            incomeSource!.employmentType === EmploymentType.Other
              ? incomeSource!.sourceOfIncome
              : undefined,
        })),
      };

      await dispatchWithUnwrap(
        saveIncomeSources({
          applicationId: application.id,
          incomeSources: incomeSourcesData,
        }),
      );

      onStepComplete();
    }
  };

  return (
    <>
      {incomeSources.map((incomeSource, id) => {
        const isMainIncomeSource = incomeSource === mainIncomeSource;

        return (
          <IncomeSourcesForm
            isAdditional={!isMainIncomeSource}
            handleRemove={() => {
              handleRemoveAdditionalIncomeSource(incomeSource);
            }}
            setValues={(values) => {
              handleDataChange(values, incomeSource);
            }}
            handleIsValidChanged={(isValid, submitFunction) => {
              handleIsValidChange(isValid, submitFunction, incomeSource);
            }}
            handleSubmit={(values) => {
              handleDataChange(values, incomeSource);
              return values;
            }}
            initialData={incomeSource.formData}
            key={id}
          />
        );
      })}
      {incomeSources.length < MAX_INCOME_SOURCES && (
        <AddIncomeSourceButton onClick={handleAddAdditionalIncomeSource}>
          Add Additional Source of Income
        </AddIncomeSourceButton>
      )}
      <ButtonWithLoadingState
        disabled={!isAllFormsValid}
        kind="primary"
        size="form"
        onClick={handleContinue}
      >
        Continue
      </ButtonWithLoadingState>
    </>
  );
};

export default IncomeSourcesStep;
